import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, PieChart, TrendingUp, Users, Calendar, Download, FileSpreadsheet, FileText } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

export default function AdminReports() {
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const renderText = (text) => {
    if (!text) return "";
    if (typeof text === "string") return text;
    return text.en || text.ar || Object.values(text)[0] || "";
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get('/admin/dashboard/reports');
      setReportData(data);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async (type) => {
    try {
      const endpoint = type === 'excel' ? '/admin/reports/export/excel' : '/admin/reports/export/pdf';
      const response = await api.get(endpoint, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = type === 'excel' ? `leads_report_${new Date().toISOString().split('T')[0]}.xlsx` : `eternal_report_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type.toUpperCase()} Report downloaded!`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-gray-500">Loading reports...</div>;
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performance Reports</h1>
          <p className="text-gray-500 text-sm mt-1">Lead generation and conversion analytics</p>
        </div>
        <div className="flex gap-2">
           <button 
             onClick={() => handleExport('excel')} 
             className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition text-sm font-medium border border-emerald-100"
           >
             <FileSpreadsheet size={16} />
             Export Excel
           </button>
           <button 
             onClick={() => handleExport('pdf')} 
             className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition text-sm font-medium border border-red-100"
           >
             <FileText size={16} />
             Export PDF
           </button>
           <button onClick={fetchReportData} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm font-medium">
             Refresh Data
           </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600">
            <Users size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Leads</p>
            <h3 className="text-2xl font-bold text-gray-900">{reportData?.total_leads}</h3>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">
            <TrendingUp size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500">Converted Leads</p>
            <h3 className="text-2xl font-bold text-gray-900">{reportData?.converted_leads}</h3>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600">
            <PieChart size={24} />
          </div>
          <div>
            <p className="text-sm text-gray-500">Conversion Rate</p>
            <h3 className="text-2xl font-bold text-gray-900">{reportData?.conversion_rate}%</h3>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Leads per Month Chart */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Calendar size={20} className="text-blue-500" />
            Leads per Month
          </h3>
          <div className="h-64 flex items-end gap-3 px-2">
            {reportData?.leads_per_month.map((item, idx) => {
               const maxCount = Math.max(...reportData.leads_per_month.map(i => i.count), 1);
               const height = (item.count / maxCount) * 100;
               return (
                 <div key={idx} className="flex-1 flex flex-col items-center gap-2 group">
                   <div 
                     className="w-full bg-blue-500 rounded-t-lg transition-all duration-500 hover:bg-blue-600 relative overflow-hidden"
                     style={{ height: `${height}%` }}
                   >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
                      <div className="opacity-0 group-hover:opacity-100 absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] py-1 px-2 rounded whitespace-nowrap transition-opacity">
                        {item.count} leads
                      </div>
                   </div>
                   <span className="text-[10px] font-medium text-gray-500 rotate-45 mt-2 origin-left">{item.month}</span>
                 </div>
               )
            })}
          </div>
          {reportData?.leads_per_month.length === 0 && (
             <div className="h-64 flex items-center justify-center text-gray-400 border border-dashed rounded-xl">
               Not enough data yet
             </div>
          )}
        </div>

        {/* Leads by Source */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <BarChart3 size={20} className="text-purple-500" />
            Leads by Source
          </h3>
          <div className="space-y-4">
            {reportData?.leads_by_source.map((item, idx) => {
              const total = reportData.total_leads || 1;
              const percentage = Math.round((item.count / total) * 100);
              return (
                <div key={idx} className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700">{renderText(item.source)}</span>
                    <span className="text-gray-500">{item.count} ({percentage}%)</span>
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 1, delay: idx * 0.1 }}
                      className="h-full bg-purple-500 rounded-full"
                    />
                  </div>
                </div>
              );
            })}
            {reportData?.leads_by_source.length === 0 && (
              <div className="py-8 text-center text-gray-400 italic">No source data available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
